import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import {
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Typography,
  TableFooter,
  TableContainer
} from '@material-ui/core';
import { imgBaseUrl } from 'configs';
import { TextFormattedNumber } from 'components';
import logo from 'components/Logo/logo-dermpro.svg';
import { singleUnitName } from 'helpers/other';
import round from 'helpers/mathRound';
import { getAlleDiscount } from '../helpers';
import { getDiscount } from '../utils/getDiscount';
import { useStyles } from './styles';


const QuoteDetails = ({task}) => {
  const classes = useStyles();
  const {
    selectedProducts,
    products,
    _id
  } = task;

  const [loaded, setLoaded] = useState(false);
  const [totalsUpdated, setTotalsUpdated] = useState(false);
  const [totalsQuoteData, setTotalsQuoteData] = useState({
    [_id]: {
      subtotal: 0,
      discount: 0,
      amount: 0
    }
  });

  const { stores = [] } = useSelector((state: IReduxState) => state.user.profile);
  const mediaStoreUrl = `${stores[0]?.apiUrl}/media/catalog/product` || imgBaseUrl;

  return (
    <TableContainer className="overflow-auto w-full">
      <Table className={classes.tableRoot}>
        <TableHead>
          <TableRow>
            <TableCell width="80px">ITEMS</TableCell>
            <TableCell width="30%" />
            <TableCell align="center">QUANTITY</TableCell>
            <TableCell align="center">SUBTOTAL</TableCell>
            <TableCell align="center">SAVINGS</TableCell>
            <TableCell align="center">TOTAL</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {selectedProducts.map((item, index) => {
            const {
              name,
              price,
              image,
              catalog_product_unit = 'units',
              subscription_active,
              subscription_label,
              quotepro_discounts,
              subscription_options,
              savings,
              short_description
            } = products[item.sku].product;

            const {
              sku,
              qty,
              price: updatedPrice,
              product_option: {
                extension_attributes
              },
              custom_subscription_price
            } = item;

            const itemPrice = updatedPrice ? updatedPrice : price;
            const savingsValue = round(Number(savings)) || 0;
            const roundedProductPrice = round(price + savingsValue) || 0;
            const unitsName = catalog_product_unit || 'units';

            const dataForDiscount = {
              savings: savingsValue,
              discount_options: quotepro_discounts ? JSON.parse(quotepro_discounts) : [],
              itemPrice
            };

            const isAlleSKU = sku.includes('bdsku');
            const discount = isAlleSKU
              ? round(getAlleDiscount(products[item.sku].product)) || 0
              : round(getDiscount(item, dataForDiscount)) || 0;

            const subscriptionData = subscription_options?.find((s) => s.id === extension_attributes?.subscription_id);
            const isNullPrice = subscriptionData?.installment_price === null && subscriptionData?.adjustment_price === null;
            let regularPrice = round(itemPrice) || 0;

            if (!!subscriptionData && subscription_active === '1' && !isNullPrice && updatedPrice == 0) {
              regularPrice = round(subscriptionData?.installment_price + subscriptionData?.adjustment_price);
            }

            const subtotal = updatedPrice
              ? updatedPrice
              : itemPrice + savingsValue;

            const amount = savingsValue
              ? (regularPrice * qty)
              : (regularPrice * qty) - discount;

            if (!totalsUpdated) {
              const totalsForItem = {
                subtotal: subtotal * qty,
                discount,
                amount
              };

              setTotalsQuoteData((prevTotals) => ({
                ...prevTotals,
                [_id]: {
                  subtotal: prevTotals[_id].subtotal + totalsForItem.subtotal,
                  discount: prevTotals[_id].discount + totalsForItem.discount,
                  amount: prevTotals[_id].amount + totalsForItem.amount
                }
              }));

              setTotalsUpdated(true);
            }

            return (
              <TableRow key={`${index}-${item._id}`}>
                <TableCell component="th" scope="row">
                  <img
                    src={loaded ? `${mediaStoreUrl}${image}` : logo}
                    alt={`${name} image`}
                    className={classes.quoteImage}
                    onLoad={() => setLoaded(true)}
                    onError={(e) => {
                      (e.target as HTMLImageElement).onerror = null;
                      (e.target as HTMLImageElement).src = logo;
                    }}
                  />
                </TableCell>
                <TableCell width="50%">
                  <Typography variant="subtitle2" className="font-semibold" gutterBottom>
                    {name}&nbsp;
                    <span className="text-12 font-italic font-medium whitespace-no-wrap">
                      (<TextFormattedNumber value={round(roundedProductPrice)} prefix="$" />/
                      {singleUnitName(unitsName)})
                    </span>
                  </Typography>
                  {subscription_active === '1' && !!subscriptionData?.frequency_count &&
                  <>
                    <div className="flex flex-wrap">
                      <span className="text-12 whitespace-no-wrap mr-1 font-italic font-bold">
                        {subscription_label}{':'}
                      </span>
                      <span className="text-12 whitespace-no-wrap font-italic">
                        {subscriptionData?.sproduct_label || ''}
                      </span>
                    </div>
                    <div className="flex flex-wrap mb-2">
                      <span className="text-12 whitespace-no-wrap mr-1 font-italic font-bold">
                        {'Periodic Price:'}
                      </span>
                      <span className="text-12 whitespace-no-wrap font-italic">
                        <TextFormattedNumber
                          value={custom_subscription_price
                            ? custom_subscription_price
                            : round(subscriptionData?.installment_price) || 0
                          }
                          prefix="$"
                        />
                      </span>
                    </div>
                  </>
                  }
                  <Typography dangerouslySetInnerHTML={{ __html: short_description || '' }} />
                </TableCell>
                <TableCell align="center">
                  <Typography variant="body2">{`${qty} ${
                    qty === 1 ? singleUnitName(unitsName) : unitsName
                  }`}
                  </Typography>
                </TableCell>
                <TableCell align="center">
                  <TextFormattedNumber value={subtotal * qty} prefix="$" />
                </TableCell>
                <TableCell align="center">
                  <TextFormattedNumber value={discount} prefix="$" />
                </TableCell>
                <TableCell align="center">
                  <TextFormattedNumber value={isAlleSKU ? 0 : amount} prefix="$" />
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
        <TableFooter className="border">
          <TableRow>
            <TableCell />
            <TableCell />
            <TableCell />
            <TableCell align="center">
              <TextFormattedNumber value={totalsQuoteData[_id]?.subtotal} prefix="$" />
            </TableCell>
            <TableCell align="center">
              <TextFormattedNumber value={totalsQuoteData[_id]?.discount} prefix="$" />
            </TableCell>
            <TableCell align="center">
              <TextFormattedNumber value={totalsQuoteData[_id]?.amount} prefix="$" />
            </TableCell>
          </TableRow>
        </TableFooter>
      </Table>
    </TableContainer>
  );
};

export default QuoteDetails;
